import { useTranslation } from "react-i18next";

import { Mark } from "./brand/Mark";
import { LanguageMenu } from "./components/LanguageMenu";
import { ScriptFont } from "./components/ScriptFont";
import type { SiteConfig } from "./config";
import type { Locale } from "./i18n/languages";
import { Home } from "./pages/Home";
import { Legal } from "./pages/Legal";
import { localeHref, SECURITY_ATLAS_HREF, type RoutePath } from "./routes";

export interface AppProps {
  locale: Locale;
  path: RoutePath;
  config: SiteConfig;
}

/**
 * There is no client-side router on purpose: every page is pre-rendered and every link is a
 * real href. A marketing site gains nothing from soft navigation and loses the guarantee
 * that what a crawler sees is what a visitor sees.
 */
export function App({ locale, path, config }: AppProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* The font for this page's writing system, if it needs one beyond latin. Rendered here
          rather than in the pre-render template because it depends on the locale; React hoists
          it into <head>. See src/components/ScriptFont.tsx. */}
      <ScriptFont locale={locale} />

      <a className="skip-link" href="#content">
        {t("site.skipToContent")}
      </a>

      <header className="site-header">
        <a className="site-header__brand" href={localeHref(locale, "/")}>
          <Mark className="site-header__mark" />
          {t("site.name")}
        </a>

        <div className="site-header__actions">
          {/* English-only page, so the link says so with hrefLang rather than pretending a
              translation exists. */}
          <a className="site-header__link" href={SECURITY_ATLAS_HREF} hrefLang="en">
            {t("nav.securityAtlas")}
          </a>
          <LanguageMenu locale={locale} path={path} />
        </div>
      </header>

      <main id="content">
        {path === "/" ? (
          <Home config={config} />
        ) : (
          <div className="container">
            <Legal page={path === "/privacy" ? "privacy" : "terms"} />
          </div>
        )}
      </main>

      <footer className="site-footer">
        <span className="site-footer__rights">{t("footer.rights")}</span>
        <a href={SECURITY_ATLAS_HREF} hrefLang="en">
          {t("nav.securityAtlas")}
        </a>
        <a href={localeHref(locale, "/privacy")}>{t("footer.privacy")}</a>
        <a href={localeHref(locale, "/terms")}>{t("footer.terms")}</a>
      </footer>
    </>
  );
}
